import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useFollowUpPlanDetail,
  useFollowUpPlanTransitions,
  FOLLOW_UP_PLAN_STATUS_LABELS,
  type FollowUpPlanDto,
} from '@/hooks/useFollowUp';
import {
  FOLLOW_UP_PERMISSIONS,
  canSeeFollowUpPlansMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

export default function FollowUpPlanDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { plan, loading, error, fetchPlan, setPlan } = useFollowUpPlanDetail();
  const transitions = useFollowUpPlanTransitions();
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelKey, setCancelKey] = useState(0);

  const canRead = canSeeFollowUpPlansMenu(permissions);
  const canUpdate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.UPDATE);

  useEffect(() => {
    if (!canRead || !id) return;
    void fetchPlan(id);
  }, [id, canRead, fetchPlan]);

  if (!canRead) return <AccessDenied resource="随访计划" />;

  if (loading || !plan) {
    return <div className="p-6 text-sm text-muted-foreground">加载中…</div>;
  }

  const runTransition = async (
    action: 'pause' | 'resume' | 'complete',
  ) => {
    const updated = await transitions.transition(action, plan.id, {
      expectedVersion: plan.version,
    });
    if (updated) setPlan(updated);
  };

  const runCancel = async () => {
    const updated = await transitions.cancel(plan.id, {
      expectedVersion: plan.version,
      reason: cancelReason.trim(),
    });
    if (updated) {
      setPlan(updated);
      setShowCancel(false);
      setCancelReason('');
    }
  };

  return (
    <div className="space-y-4 p-6">
      <Button variant="ghost" onClick={() => navigate('/follow-up/plans')}>
        ← 返回随访计划列表
      </Button>
      <Card className="space-y-2 p-4">
        <h1 className="text-xl font-semibold">{plan.title}</h1>
        <div className="text-sm text-muted-foreground">
          客户 {plan.customerId} · 状态 {FOLLOW_UP_PLAN_STATUS_LABELS[plan.status]}
        </div>
        <PlanMetadata plan={plan} />
        {plan.objective && (
          <div className="text-sm">
            <span className="text-muted-foreground">目标：</span>
            {plan.objective}
          </div>
        )}
        {plan.notes && (
          <div className="text-sm">
            <span className="text-muted-foreground">备注：</span>
            {plan.notes}
          </div>
        )}
        {plan.closeReason && (
          <div className="text-sm">
            <span className="text-muted-foreground">关闭原因：</span>
            {plan.closeReason}
          </div>
        )}
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}
      {transitions.error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {transitions.error.message}
        </Card>
      )}

      {canUpdate && (
        <Card className="space-y-2 p-4">
          <h2 className="text-base font-semibold">状态操作</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={plan.status !== 'active' || transitions.loading}
              onClick={() => runTransition('pause')}
            >
              暂停
            </Button>
            <Button
              disabled={plan.status !== 'paused' || transitions.loading}
              onClick={() => runTransition('resume')}
            >
              恢复
            </Button>
            <Button
              disabled={plan.status !== 'active' || transitions.loading}
              onClick={() => runTransition('complete')}
            >
              标记完成
            </Button>
            <Button
              variant="outline"
              disabled={
                !(plan.status === 'active' || plan.status === 'paused') ||
                transitions.loading
              }
              onClick={() => {
                setCancelKey((k) => k + 1);
                setShowCancel(true);
              }}
            >
              取消计划
            </Button>
          </div>
          {showCancel && (
            <div key={cancelKey} className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <Label htmlFor="cancel-reason">取消原因（必填）</Label>
              <Input
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCancel(false)}>
                  收起
                </Button>
                <Button
                  onClick={runCancel}
                  disabled={!cancelReason.trim() || transitions.loading}
                >
                  确认取消
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function PlanMetadata({ plan }: { plan: FollowUpPlanDto }) {
  const items: Array<[string, string | undefined]> = [
    ['服务用户', plan.serviceUserId ?? undefined],
    ['订单', plan.orderId ?? undefined],
    ['订单项目', plan.orderProjectId ?? undefined],
    ['起始日期', plan.startDate ? new Date(plan.startDate).toLocaleDateString() : undefined],
    ['结束日期', plan.endDate ? new Date(plan.endDate).toLocaleDateString() : undefined],
    ['节奏', plan.cadenceHint ?? undefined],
    ['负责人', plan.ownerUserId],
    ['版本', String(plan.version)],
  ].filter((v): v is [string, string] => Boolean(v[1]));
  return (
    <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-3">
      {items.map(([label, value]) => (
        <div key={label}>
          <span className="text-muted-foreground">{label}：</span>
          {value}
        </div>
      ))}
    </div>
  );
}
